<template>
  <div id="top" class="report-page">
    <div class="report-page-intro">
      <UiPageHeader title="Reports" description="Review client performance, scheme utilization, and claims across your reporting scope." />
    </div>
    <div class="report-page-layout">
      <nav class="report-page-sidebar" aria-label="Report sections">
        <button v-for="tab in tabsState" :key="tab.id" type="button" class="report-page-nav-item"
          :class="{ 'is-active': tab.id === currState }" :aria-current="tab.id === currState ? 'page' : undefined"
          aria-controls="report-workspace" @click="handleActiveState(tab.id)">
          <i class="material-icons" aria-hidden="true">{{ tab.icon }}</i><span>{{ tab.title }}</span>
        </button>
      </nav>
      <section id="report-workspace" class="report-page-content" :aria-label="tabsState.find(tab => tab.id === currState)?.title || 'Report workspace'">
        <ReportInsight v-if="currState == 1" />
        <ClientsInsight :key="clientsInsightKey" v-if="currState == 2" />
        <SchemesInsight :key="schemesInsightKey" v-if="currState == 3" />
        <ClaimsInsight v-if="currState == 4" />
      </section>
    </div>
  </div>
</template>
<script>
import { onMounted, ref } from "vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import ClientsInsight from "@/components/insight/ClientsInsightComponent.vue";
import SchemesInsight from "@/components/insight/SchemesInsightComponent.vue";
import ReportInsight from "@/components/insight/ReportInsightComponent.vue";
import ClaimsInsight from "@/components/insight/ClaimsInsightComponent.vue";

export default {
  name: "reportHome",
  components: { UiPageHeader, ClientsInsight, SchemesInsight, ReportInsight, ClaimsInsight },
  setup() {
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const tabsState = ref([]);
    const currState = ref(null);
    const clientsInsightKey = ref(0);
    const schemesInsightKey = ref(0);
    const { routeToTab } = useModuleNavigation();

    // start
    onMounted(() => {
      // page name
      document.title = "Insights";
    });

    // handle back
    const handleBack = () => {
      // restart menu
      currTab.value = 1;
    };

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Insights", title: "All", icon: null, url: "report", hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.id === 1);
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // start
    onMounted(() => {
      // load tab
      tabsState.value.push({ id: 1, title: "Executive", icon: "dashboard" });
      tabsState.value.push({ id: 2, title: "Clients", icon: "business" });
      tabsState.value.push({ id: 3, title: "Schemes", icon: "layers" });
      tabsState.value.push({ id: 4, title: "Utilization Report", icon: "insert_chart" });
      // set default
      currState.value = 1;
    });

    // handle curr status
    const handleActive = (tab) => {
      currTab.value = tab;
      currState.value = 1;
      routeToTab(tab, { replace: true });
    };

    // handle curr status
    const handleActiveState = (tab) => {
      if (tab === 2) {
        clientsInsightKey.value += 1;
      }

      if (tab === 3) {
        schemesInsightKey.value += 1;
      }

      // update show and components
      currState.value = tab;
    };

    return {
      home,
      handleActive,
      handleActiveState,
      handleBack,
      currTab,
      currState,
      clientsInsightKey,
      schemesInsightKey,
      tabs,
      tabsState,
    };
  },
};
</script>

<style scoped>
.report-page { box-sizing: border-box; margin: 0 auto; max-width: var(--ui-content-width); padding: 2rem 2rem 3rem; width: 100%; }
.report-page-intro { border-bottom: 1px solid var(--ui-border); margin-bottom: 2.5rem; padding-bottom: 1.75rem; }
.report-page-intro :deep(.ui-page-header) { margin-bottom: 0; }
.report-page-intro :deep(.ui-page-header__title) { font-size: clamp(1.75rem, 2.1vw, 2rem); font-weight: 700; letter-spacing: -.025em; }
.report-page-intro :deep(.ui-page-header__description) { font-size: 1rem; line-height: 1.55; }
.report-page-layout { align-items: start; display: grid; gap: 3.5rem; grid-template-columns: 15rem minmax(0, 1fr); }
.report-page-sidebar { display: flex; flex-direction: column; gap: .25rem; }
.report-page-nav-item { align-items: center; background: transparent; border: 0; border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); cursor: pointer; display: grid; font-family: var(--ui-font-sans); font-size: .875rem; font-weight: 500; gap: .75rem; grid-template-columns: 1.25rem minmax(0, 1fr); min-height: 2.75rem; padding: .6rem .75rem; text-align: left; width: 100%; }
.report-page-nav-item i { font-size: 1.125rem; }
.report-page-nav-item:is(:hover, .is-active) { background: var(--ui-muted); }
.report-page-nav-item.is-active { font-weight: 600; }
.report-page :is(button, a):focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 2px; }
.report-page-content { min-width: 0; }
@layer components {
  .report-page :deep(.executive-head), .report-page :deep(.client-insight-head), .report-page :deep(.schemes-insight-head), .report-page :deep(.claims-report-head) {
    background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 0 0 1.25rem !important;
  }
  .report-page :deep(.executive-head label), .report-page :deep(.client-insight-head label), .report-page :deep(.schemes-insight-head label), .report-page :deep(.claims-report-head label), .report-page :deep(.filter-title label) { display: none !important; }
  .report-page :deep(.executive-head h5), .report-page :deep(.client-insight-head h5), .report-page :deep(.schemes-insight-head h5), .report-page :deep(.claims-report-head h5) { color: var(--ui-foreground) !important; font-size: 1.125rem !important; font-weight: 600 !important; margin: 0 0 .5rem !important; }
  .report-page :deep(.executive-head p), .report-page :deep(.client-insight-head p), .report-page :deep(.schemes-insight-head p), .report-page :deep(.claims-report-head p) { color: var(--ui-muted-foreground) !important; font-size: .8125rem !important; line-height: 1.6 !important; margin: 0 !important; }
  .report-page :deep(.filter-panel) { background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 1.25rem 0 !important; }
  .report-page :deep(.filter-title h6) { color: var(--ui-foreground) !important; font-size: .875rem !important; font-weight: 600 !important; margin: 0 !important; }
  .report-page :deep(.filter-grid) { align-items: end !important; gap: 1rem !important; grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr)) !important; }
  .report-page :deep(.filter-grid .input-field) { display: flex !important; flex-direction: column !important; margin: 0 !important; min-width: 0; }
  .report-page :deep(.filter-grid .input-field > label) { color: var(--ui-foreground) !important; font-size: .75rem !important; margin: 0 0 .5rem !important; order: -1; position: static !important; transform: none !important; }
  .report-page :deep(.filter-grid .report-actions) { align-items: center; display: flex; flex-wrap: wrap; gap: .5rem; grid-column: 1 / -1; justify-content: flex-end; }
  .report-page :deep(.report-shell > .filter-panel .filter-grid) { display: flex !important; flex-wrap: wrap; }
  .report-page :deep(.report-shell > .filter-panel .filter-grid > div:not(.report-actions)) { flex: 1 1 12rem; min-width: 0; }
  .report-page :deep(.report-shell > .filter-panel .filter-grid > .report-actions) { flex: 0 1 auto; justify-content: flex-start; margin: 0 !important; }
  .report-page :deep(.client-insight-shell > .filter-panel .filter-grid) { display: flex !important; flex-wrap: wrap; }
  .report-page :deep(.client-insight-shell > .filter-panel .filter-grid > div:not(.report-actions)) { flex: 1 1 12rem; min-width: 0; }
  .report-page :deep(.client-insight-shell > .filter-panel .filter-grid > .report-actions) { flex: 1 1 100%; justify-content: flex-start; margin: 0 !important; }
  .report-page :deep(.schemes-insight-shell > .filter-panel .filter-grid) { display: flex !important; flex-wrap: wrap; }
  .report-page :deep(.schemes-insight-shell > .filter-panel .filter-grid > div:not(.report-actions)) { flex: 1 1 12rem; min-width: 0; }
  .report-page :deep(.schemes-insight-shell > .filter-panel .filter-grid > .report-actions) { flex: 1 1 100%; justify-content: flex-start; margin: 0 !important; }
  .report-page :deep(.filter-grid .input-field > input), .report-page :deep(.filter-grid .input-field > select) { box-sizing: border-box; margin: 0 !important; min-height: 2.65rem; width: 100%; }
  .report-page :deep(.filter-panel button) { font-size: .8125rem !important; font-weight: 600 !important; gap: .5rem; }
  .report-page :deep(.filter-panel button i) { font-size: 1rem !important; margin: 0 !important; }
  .report-page :deep(.claims-report-head::before) { display: none !important; }
  .report-page :deep(.claims-report-shell > form) { background: transparent !important; border: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
  .report-page :deep(.claims-report-shell .report-field-grid) { display: grid; gap: 1.25rem; grid-template-columns: repeat(auto-fit, minmax(min(100%, 16rem), 1fr)); padding: 1.5rem 0; }
  .report-page :deep(.claims-report-shell .report-form-panel) { background: transparent !important; border: 0 !important; box-shadow: none !important; margin: 0 !important; min-width: 0; padding: 0 !important; }
  .report-page :deep(.claims-report-shell .report-field) { display: flex !important; flex-direction: column; gap: .5rem; margin: 0 !important; }
  .report-page :deep(.claims-report-shell .report-field-label) { color: var(--ui-foreground) !important; font-size: .75rem !important; line-height: 1.5; margin: 0 !important; position: static !important; transform: none !important; }
  .report-page :deep(.claims-report-shell select) { box-sizing: border-box; margin: 0 !important; min-height: 2.65rem; width: 100%; }
  .report-page :deep(.claims-report-shell .report-submit-strip) { background: transparent !important; border: 0 !important; border-top: 1px solid var(--ui-border) !important; border-radius: 0 !important; display: flex; margin: 0 !important; padding: 1.25rem 0 !important; }
  .report-page :deep(.claims-report-shell .report-submit-strip button) { align-items: center; display: inline-flex; font-size: .8125rem !important; gap: .5rem; min-height: 2.5rem; min-width: 0 !important; padding: .6rem 1rem !important; width: auto !important; }
  .report-page :deep(.claims-report-shell .report-submit-strip button i) { font-size: 1rem !important; margin: 0 !important; }
  .report-page :deep(.summary-panel), .report-page :deep(.secondary-panel), .report-page :deep(.report-section), .report-page :deep(.report-panel), .report-page :deep(.highlight-panel), .report-page :deep(.summary-tile) { border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; box-shadow: none !important; min-width: 0; transform: none !important; }
  .report-page :deep(.client-card), .report-page :deep(.scheme-card) { border-color: var(--ui-border) !important; box-shadow: none !important; transform: none !important; }
  .report-page :deep(.summary-panel h6) { font-size: 1.125rem !important; font-weight: 600 !important; }
  .report-page :deep(.summary-panel small) { font-size: .75rem !important; }
  .report-page :deep(.scope-chip) { background: var(--ui-secondary) !important; border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; color: var(--ui-muted-foreground) !important; font-size: .75rem !important; }
  .report-page :deep(a), .report-page :deep(a *), .report-page :deep(.material-icons) { text-decoration: none !important; }
}
@media (max-width: 1100px) { .report-page { padding: 1.5rem 1.5rem 2.5rem; } .report-page-layout { gap: 2rem; grid-template-columns: 13rem minmax(0, 1fr); } }
@media (max-width: 900px) { .report-page-layout { gap: 1.5rem; grid-template-columns: minmax(0, 1fr); } .report-page-sidebar { border-bottom: 1px solid var(--ui-border); display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); padding-bottom: 1rem; } }
@media (max-width: 600px) { .report-page { padding: 1.25rem 1rem 2rem; } }
.spacer {
  padding: 2px;
}

.pointer {
  cursor: default;
}

/* display */
.tab-custom-section {
  padding: 20px 0 0;
  border-top: 1px solid #ddd;
}

.insight-page-shell {
  margin-top: -14px;
  padding-bottom: 44px;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.insight-workbench {
  color: #263238;
}

.insight-top-card {
  align-items: center;
  background: #fff;
  border: 1px solid #c4d3d9;
  border-bottom-color: #e2e8eb;
  border-radius: 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  display: grid;
  gap: 32px;
  grid-template-columns: minmax(260px, 0.92fr) minmax(420px, 1.25fr);
  margin-bottom: 18px;
  overflow: visible;
  padding: 28px 32px;
  position: relative;
  z-index: 2;
}

.insight-top-card::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.insight-top-card > * {
  position: relative;
  z-index: 2;
}

.insight-top-card,
.insight-workbench :deep(.report-shell),
.insight-workbench :deep(.client-insight-shell),
.insight-workbench :deep(.schemes-insight-shell),
.insight-workbench :deep(.scheme-detail-shell),
.insight-workbench :deep(.client-detail-stack),
.insight-workbench :deep(.principal-detail-shell),
.insight-workbench :deep(.claims-report-shell),
.insight-workbench :deep(.executive-head),
.insight-workbench :deep(.client-insight-head),
.insight-workbench :deep(.schemes-insight-head),
.insight-workbench :deep(.claims-report-head),
.insight-workbench :deep(.filter-panel),
.insight-workbench :deep(.secondary-panel),
.insight-workbench :deep(.report-section),
.insight-workbench :deep(.report-panel),
.insight-workbench :deep(.highlight-panel),
.insight-workbench :deep(.summary-panel),
.insight-workbench :deep(.summary-tile),
.insight-workbench :deep(.metric-cell),
.insight-workbench :deep(.client-card),
.insight-workbench :deep(.scheme-card),
.insight-workbench :deep(.card-panel),
.insight-workbench :deep(.report-form-panel),
.insight-workbench :deep(.report-submit-strip) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.insight-workbench :deep(.report-shell),
.insight-workbench :deep(.client-insight-shell),
.insight-workbench :deep(.schemes-insight-shell),
.insight-workbench :deep(.scheme-detail-shell),
.insight-workbench :deep(.client-detail-stack),
.insight-workbench :deep(.principal-detail-shell),
.insight-workbench :deep(.claims-report-shell) {
  gap: 16px;
  margin-top: 0;
}

.insight-workbench :deep(.executive-head),
.insight-workbench :deep(.client-insight-head),
.insight-workbench :deep(.schemes-insight-head),
.insight-workbench :deep(.claims-report-head),
.insight-workbench :deep(.filter-panel),
.insight-workbench :deep(.secondary-panel),
.insight-workbench :deep(.report-section),
.insight-workbench :deep(.report-panel),
.insight-workbench :deep(.highlight-panel),
.insight-workbench :deep(.summary-panel),
.insight-workbench :deep(.summary-tile),
.insight-workbench :deep(.metric-cell),
.insight-workbench :deep(.client-card),
.insight-workbench :deep(.scheme-card),
.insight-workbench :deep(.card-panel),
.insight-workbench :deep(.report-form-panel),
.insight-workbench :deep(.report-submit-strip) {
  border: 1px solid #c4d3d9 !important;
  border-radius: 6px !important;
  box-sizing: border-box;
}

.insight-workbench :deep(.client-detail-stack > .card-panel),
.insight-workbench :deep(.principal-detail-shell > .card-panel),
.insight-workbench :deep(.scheme-detail-shell > .card-panel),
.insight-workbench :deep(.scheme-detail-shell > .report-section) {
  margin: 0;
  width: 100%;
}

.insight-workbench :deep(.client-detail-stack > .card-panel + .card-panel),
.insight-workbench :deep(.principal-detail-shell > .card-panel + .card-panel),
.insight-workbench :deep(.scheme-detail-shell > .card-panel + .card-panel),
.insight-workbench :deep(.scheme-detail-shell > .card-panel + .report-section),
.insight-workbench :deep(.scheme-detail-shell > .report-section + .report-section) {
  border-top: 1px solid #c4d3d9 !important;
  border-radius: 6px !important;
}

.insight-top-card + :deep(.client-detail-stack),
.insight-top-card + :deep(.scheme-detail-shell) {
  gap: 16px;
  margin-top: -18px;
}

.insight-top-card + :deep(.client-detail-stack > .card-panel:first-child),
.insight-top-card + :deep(.scheme-detail-shell > :first-child) {
  border-top: 0 !important;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.insight-title-block h5 {
  color: #263238;
  font-weight: 700;
  line-height: 1.18;
  margin: 8px 0;
}

.insight-title-block p {
  color: #607d8b;
  font-size: 1.03rem;
  line-height: 1.45;
  margin: 0;
  max-width: 560px;
}

.insight-tab-bar {
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow: hidden;
}

.insight-tab-bar button {
  align-items: center;
  background: #f8fafb;
  border: 0;
  border-right: 1px solid #dce4e8;
  color: #546e7a;
  cursor: pointer;
  display: flex;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  gap: 10px;
  justify-content: center;
  min-height: 76px;
  padding: 0 14px;
  transition: background 0.18s ease, color 0.18s ease;
}

.insight-tab-bar button:last-child {
  border-right: 0;
}

.insight-tab-bar button:hover {
  background: #eef4f6;
  color: #263238;
}

.insight-tab-bar button.is-active {
  background: #26383f;
  color: #fff;
}

.insight-tab-bar i {
  color: #00897b;
  font-size: 1.25rem;
}

.insight-tab-bar button.is-active i {
  color: #fff;
}

@media only screen and (max-width: 992px) {
  .insight-top-card {
    grid-template-columns: 1fr;
    padding: 24px;
  }

  .insight-tab-bar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 600px) {
  .insight-top-card {
    border-radius: 6px;
    padding: 22px 18px;
  }

  .insight-page-shell {
    width: 94%;
  }

  .insight-title-block h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .insight-title-block p {
    font-size: 0.95rem;
  }

  .insight-tab-bar {
    grid-template-columns: 1fr;
  }

  .insight-tab-bar button {
    justify-content: flex-start;
    min-height: 54px;
  }

  .insight-workbench :deep(.executive-head),
  .insight-workbench :deep(.client-insight-head),
  .insight-workbench :deep(.schemes-insight-head),
  .insight-workbench :deep(.claims-report-head),
  .insight-workbench :deep(.filter-panel),
  .insight-workbench :deep(.secondary-panel),
  .insight-workbench :deep(.report-section),
  .insight-workbench :deep(.report-panel),
  .insight-workbench :deep(.highlight-panel),
  .insight-workbench :deep(.summary-panel),
  .insight-workbench :deep(.client-card),
  .insight-workbench :deep(.scheme-card),
  .insight-workbench :deep(.card-panel) {
    border-radius: 6px !important;
  }

  .insight-workbench :deep(table) {
    min-width: 680px;
  }

  .insight-workbench :deep(.responsive-table),
  .insight-workbench :deep(.table-wrap),
  .insight-workbench :deep(.report-table-wrap) {
    overflow-x: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .insight-top-card,
  .insight-workbench :deep(.report-shell),
  .insight-workbench :deep(.client-insight-shell),
  .insight-workbench :deep(.schemes-insight-shell),
  .insight-workbench :deep(.scheme-detail-shell),
  .insight-workbench :deep(.client-detail-stack),
  .insight-workbench :deep(.principal-detail-shell),
  .insight-workbench :deep(.claims-report-shell),
  .insight-workbench :deep(.executive-head),
  .insight-workbench :deep(.client-insight-head),
  .insight-workbench :deep(.schemes-insight-head),
  .insight-workbench :deep(.claims-report-head),
  .insight-workbench :deep(.filter-panel),
  .insight-workbench :deep(.secondary-panel),
  .insight-workbench :deep(.report-section),
  .insight-workbench :deep(.report-panel),
  .insight-workbench :deep(.highlight-panel),
  .insight-workbench :deep(.summary-panel),
  .insight-workbench :deep(.summary-tile),
  .insight-workbench :deep(.metric-cell),
  .insight-workbench :deep(.client-card),
  .insight-workbench :deep(.scheme-card),
  .insight-workbench :deep(.card-panel),
  .insight-workbench :deep(.report-form-panel),
  .insight-workbench :deep(.report-submit-strip) {
    animation: none;
  }
}
</style>
